// chatbot-firebase.js
// Creates a session ONLY on the first non-empty message.
// Session + first message are written atomically to prevent blank logs.

window.chatSessionStarted = false; // kept for compatibility
let currentSessionId = null;

// --- Firebase Integration ---
import { initializeApp } from "https://www.gstatic.com/firebasejs/11.3.1/firebase-app.js";
import { getAnalytics } from "https://www.gstatic.com/firebasejs/11.3.1/firebase-analytics.js";
import { getAuth, signInAnonymously, onAuthStateChanged } from "https://www.gstatic.com/firebasejs/11.3.1/firebase-auth.js";
import {
  getFirestore, doc, setDoc, collection, serverTimestamp,
  writeBatch, increment
} from "https://www.gstatic.com/firebasejs/11.3.1/firebase-firestore.js";

// --- Your Firebase configuration ---
const firebaseConfig = {
  apiKey: "AIzaSyBoiOBri3L_U39jgHDj2PdePW4u1KNig5o",
  authDomain: "askaq-805c5.firebaseapp.com",
  projectId: "askaq-805c5",
  storageBucket: "askaq-805c5.firebasestorage.app",
  messagingSenderId: "11279561440",
  appId: "1:11279561440:web:85772baeed0bb50d8f9685",
  measurementId: "G-XJTWP50G73"
};

// --- Init ---
const appFirebase = initializeApp(firebaseConfig);
const analytics = getAnalytics(appFirebase);
const auth = getAuth(appFirebase);
const db = getFirestore(appFirebase);

// Anonymous auth (once)
onAuthStateChanged(auth, (user) => {
  if (!user) {
    signInAnonymously(auth).catch((error) => {
      console.error("Error signing in anonymously:", error);
    });
  }
});

// --- Helpers ---
const pad = (n) => String(n).padStart(2, "0");

function generateSessionId() {
  const now = new Date();
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())} ${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`;
}

function getTimePrefix() {
  const now = new Date();
  return `${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`;
}

function isNonEmpty(text) {
  return typeof text === "string" && text.trim().length > 0;
}

// --- Core: create session + first message atomically ---
async function createSessionWithFirstInteraction(role, message) {
  const text = (message ?? "").trim();
  if (!isNonEmpty(text)) return null; // never create on blanks

  const sessionId = generateSessionId();
  const sessionRef = doc(db, "sessions", sessionId);

  // Build message id: HH:MM:SS + random Firestore id
  const autoId = doc(collection(db, "sessions", sessionId, "messages")).id;
  const messageId = `${getTimePrefix()}-${autoId}`;
  const messageRef = doc(db, "sessions", sessionId, "messages", messageId);

  const now = serverTimestamp();

  const batch = writeBatch(db);
  batch.set(sessionRef, {
    startTime: now,
    hasInteraction: true,
    firstMessageAt: now,
    lastMessageAt: now,
    lastMessagePreview: text.slice(0, 200),
    messageCount: 1
  });
  batch.set(messageRef, {
    role: role || "user",
    message: text,
    timestamp: now
  });

  await batch.commit();

  currentSessionId = sessionId;
  window.chatSessionStarted = true;
  return sessionId;
}

// --- Append follow-up messages (message + session metadata update) ---
async function appendInteraction(role, message) {
  const text = (message ?? "").trim();
  if (!isNonEmpty(text)) return; // ignore blanks

  if (!currentSessionId) {
    await createSessionWithFirstInteraction(role, text);
    return;
  }

  const sessionRef = doc(db, "sessions", currentSessionId);
  const msgAutoId = doc(collection(db, "sessions", currentSessionId, "messages")).id;
  const msgId = `${getTimePrefix()}-${msgAutoId}`;
  const messageRef = doc(db, "sessions", currentSessionId, "messages", msgId);

  const batch = writeBatch(db);
  batch.set(messageRef, {
    role: role || "user",
    message: text,
    timestamp: serverTimestamp()
  });
  batch.set(sessionRef, {
    hasInteraction: true,
    lastMessageAt: serverTimestamp(),
    lastMessagePreview: text.slice(0, 200),
    messageCount: increment(1)
  }, { merge: true });

  await batch.commit();
}

// --- Public API used by your UI code ---
// Call with either 'user' or 'bot' and the text content.
// If first call, this will create the session atomically with the first message.
async function logInteraction(role, message) {
  if (!currentSessionId) {
    await createSessionWithFirstInteraction(role, message);
  } else {
    await appendInteraction(role, message);
  }
}

// Legacy no-op to avoid pre-creating sessions on open.
async function startSession() {
  console.warn("[chatbot] startSession() is deprecated. Sessions are created on first non-empty message.");
}

window.logInteraction = logInteraction;
window.startSession = startSession;
